import { useEffect } from "react";
import { TRACKER_COLORS as C } from "../../constants/trackerUi";
import useBodyScrollLock from "../../hooks/useBodyScrollLock";

/**
 * The dialog shell Tracker uses for anything that needs a decision before it
 * commits. Handoff is the reason it exists: as an inline panel it opened
 * *below* the button that summoned it, so the form could sit off-screen and
 * the stage would quietly stay put — the click looked done when it wasn't.
 *
 * Chrome matches the Raise-requirement dialog in Tracker.js: same scrim, same
 * radius, same header/body/footer split with a fixed footer so the actions
 * stay reachable however long the body grows.
 */
export default function TrackerModal({ title, subtitle, onClose, children, footer, maxWidth = 620 }) {
  // Mounted means open, so the background stays frozen for as long as it lives.
  useBodyScrollLock(true);

  // Escape closes. The Raise dialog predates this and only closes on scrim
  // click; anything opened from a keyboard-driven flow needs the key too.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "1rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface,
          borderRadius: 16,
          width: "100%",
          maxWidth,
          maxHeight: "calc(100vh - 2rem)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
        }}
      >
        <div
          style={{
            padding: "18px 24px",
            borderBottom: `1px solid ${C.line}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700 }}>{title}</h3>
            {subtitle ? (
              <p style={{ margin: "3px 0 0", fontSize: "0.8rem", color: C.inkFaint }}>{subtitle}</p>
            ) : null}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              border: "none",
              background: "none",
              fontSize: 22,
              cursor: "pointer",
              color: C.inkFaint,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1 }}>{children}</div>

        {footer ? (
          <div
            style={{
              display: "flex",
              gap: 9,
              padding: "14px 24px",
              borderTop: `1px solid ${C.line}`,
              background: C.sunken,
              flexShrink: 0,
            }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
